<template>
  <div>
    <v-app-bar color="primary" dense dark>
      <v-toolbar-title>
        <v-icon class="mr-2">fas fa-file-invoice</v-icon>Facturación.cl
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn @click="refresh" text small fab :disabled="loading"><v-icon small>fas fa-arrows-rotate</v-icon></v-btn>
      <v-btn text @click="$emit('close')" small fab><v-icon small>fas fa-close</v-icon></v-btn>
    </v-app-bar>

    <div class="d-flex justify-center align-items-center mt-6" v-if="loading">
      <v-progress-circular indeterminate color="primary" class="mr-2" />
    </div>

    <ValidationObserver v-slot="{handleSubmit}">
    <v-form v-if="!loading" class="mt-8 pa-4" @submit.prevent="handleSubmit(confirm)">
      <v-row v-if="allow_search_ot && !sales_note.id">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="otSearch"
            label="N° Orden de Trabajo"
            outlined dense hide-details
            @keydown.enter.prevent="loadFromOt"
          />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center">
          <v-btn color="primary" @click="loadFromOt" :loading="loadingOt">Cargar OT</v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="3">
          <v-select
            dense outlined hide-details
            :items="tipos"
            item-text="text"
            item-value="value"
            label="Tipo de documento"
            v-model="tipo"
          />
        </v-col>
        <v-col cols="12" md="3" v-if="tipo === 52">
          <v-select
            dense outlined hide-details
            :items="traslados"
            item-text="text"
            item-value="value"
            label="Indicador de traslado"
            v-model="tipo_traslado"
          />
        </v-col>
        <v-col cols="12" md="3" v-if="tipo === 52">
          <v-select
            dense outlined hide-details
            :items="despachos"
            item-text="text"
            item-value="value"
            label="Tipo de despacho"
            v-model="tipo_despacho"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            :value="customer_data?customer_data.rut:' '"
            label="RUT"
            dense outlined readonly hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            dense outlined readonly hide-details
            label="Razón Social"
            :value="customer_data?customer_data.name:' '"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            dense outlined readonly hide-details
            label="Giro"
            :value="customer_data?customer_data.giro:''"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            dense outlined
            :items="customer_data?customer_data.addresses:[]"
            item-text="text"
            item-value="id"
            label="Dirección"
            v-model="direccion"
          />
        </v-col>
        <v-col cols="12" md="3" v-if="tipo === 33">
          <v-text-field
            dense outlined
            type="date"
            label="Vencimiento"
            v-model="fecha_pago"
            :min="today"
            :hint="vencimientoHint"
            persistent-hint
            @change="onVencimientoChange"
          />
        </v-col>
        <v-col cols="12" md="3" v-if="tipo === 33">
          <v-select
            dense outlined hide-details
            label="Condición de pago"
            :items="paymentOptions"
            v-model="payment_days"
            @change="onPaymentDaysChange"
          />
        </v-col>
      </v-row>

      <v-row v-if="tipo === 61">
        <v-col cols="12" md="3">
          <v-select
            dense outlined hide-details
            :items="refTipos"
            item-text="text"
            item-value="value"
            label="Documento de referencia"
            v-model="referencia_tipo"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field dense outlined hide-details label="Folio referencia" v-model="referencia_folio" />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field dense outlined hide-details type="date" label="Fecha referencia" v-model="referencia_fecha" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            dense outlined hide-details
            :items="motivos"
            item-text="text"
            item-value="value"
            label="Motivo"
            v-model="motivo"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="productos"
            item-key="key"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:no-data>
              <v-alert :value="true" icon="fas fa-info-circle" class="ma-4">
                No hay productos para mostrar
              </v-alert>
            </template>
            <template v-slot:item.qty="{ item }">
              <v-text-field type="number" v-model="item.qty" :min="1" solo dense style="width:100px" hide-details />
            </template>
            <template v-slot:item.price="{ item }">
              <v-text-field type="number" v-model="item.price" :min="0" solo dense style="width:150px" hide-details />
            </template>
            <template v-slot:item.total="{ item }">
              {{ item.price * item.qty | currency('$', 0) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon class="error--text" x-small @click.stop="removeItem(item)">fas fa-trash</v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <v-row class="justify-center mt-4">
        <v-col>
          <v-card class="py-6">
            <v-simple-table dense class="strip-table">
              <thead>
                <tr>
                  <th class="text-center table-header2">Neto</th>
                  <th class="text-center table-header2">IVA</th>
                  <th class="text-center table-header2">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-center strip-body"><strong>{{ neto | currency('$', 0) }}</strong></td>
                  <td class="text-center strip-body d-flex align-center justify-center" style="min-height:80px;">
                    (<v-text-field type="number" v-model="iva" dense solo style="max-width:70px;" hide-details max="100" min="0" />)%
                    <strong class="ml-4">{{ (neto)*iva*0.01 | currency('$', 0) }}</strong>
                  </td>
                  <td class="text-center strip-body"><strong>{{ neto*(1+iva/100) | currency('$', 0) }}</strong></td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col class="text-center mt-4">
          <v-btn color="success" type="submit" :disabled="!sales_note.id" :loading="sending">
            <v-icon class="mr-2">fas fa-file-arrow-up</v-icon>Emitir
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    </ValidationObserver>

    <confirm
      v-model="showConfirm"
      :message="confirmMessage"
      @ok="sendData"
      @cancel="showConfirm=false"
    />
  </div>
</template>
<script>
import { getSalesNote } from '@/api/salesNotes.js'
import { getWorkOrder } from '@/api/production'
import { createDocument, downloadDocumentPdf } from '@/api/facturacioncl.js'
import notifications from "../../utils/notifications"
import moment from 'moment'

export default {
  name: 'FacturacionClCreateDocument',
  props: {
    sales_note_id: { required: false, default: null },
    allow_search_ot: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      loadingOt: false,
      showConfirm: false,
      sending: false,
      customer_data: {},
      sales_note: {},
      direccion: '',
      otSearch: '',
      tipo: 33,
      tipo_traslado: 1,
      tipo_despacho: 1,
      referencia_tipo: 33,
      referencia_folio: '',
      referencia_fecha: moment().format('YYYY-MM-DD'),
      motivo: 1,
      productos: [],
      fecha_pago: moment().format('YYYY-MM-DD'),
      payment_days: 0,
      iva: parseInt(this.$store.getters.getCompany.tax),
      tipos: [
        { value: 33, text: 'Factura electrónica (33)' },
        { value: 39, text: 'Boleta electrónica (39)' },
        { value: 52, text: 'Guía de despacho (52)' },
        { value: 61, text: 'Nota de crédito (61)' },
      ],
      refTipos: [
        { value: 33, text: 'Factura 33' },
        { value: 39, text: 'Boleta 39' },
        { value: 52, text: 'Guía 52' },
      ],
      motivos: [
        { value: 1, text: '1 - Anula documento' },
        { value: 2, text: '2 - Corrige texto' },
        { value: 3, text: '3 - Corrige montos' },
      ],
      traslados: [
        { value: 1, text: '1 - Operación constituye venta' },
        { value: 2, text: '2 - Ventas por efectuar' },
        { value: 3, text: '3 - Consignaciones' },
        { value: 4, text: '4 - Entrega gratuita' },
        { value: 5, text: '5 - Traslados internos' },
        { value: 6, text: '6 - Otros traslados no venta' },
        { value: 7, text: '7 - Guía de devolución' },
      ],
      despachos: [
        { value: 1, text: '1 - Despacho por cuenta del receptor' },
        { value: 2, text: '2 - Por cuenta del emisor a cliente' },
        { value: 3, text: '3 - Por cuenta del emisor a otras instalaciones' },
      ],
      headers: [
        { text: 'Código', align: 'start', sortable: false, value: 'sku', class: 'table-header2' },
        { text: 'Nombre', align: 'start', sortable: false, value: 'name', class: 'table-header2' },
        { text: 'Cantidad', align: 'end', sortable: false, value: 'qty', width: '10%', class: 'table-header2' },
        { text: 'Precio Unit.', align: 'end', sortable: false, value: 'price', width: '12%', class: 'table-header2' },
        { text: 'Total', align: 'end', sortable: false, value: 'total', class: 'table-header2' },
        { text: '', align: 'end', sortable: false, value: 'actions', class: 'table-header2' },
      ],
    }
  },
  computed: {
    neto() {
      return this.productos.reduce((acc, val) => acc + parseFloat(val.qty) * Math.round(parseFloat(val.price)), 0)
    },
    confirmMessage() {
      const labels = { 33: 'factura', 39: 'boleta', 52: 'guía', 61: 'nota de crédito' }
      const ot = this.sales_note.work_order || '-'
      return `Se emitirá una ${labels[this.tipo] || 'DTE'} en Facturación.cl vinculada a la OT ${ot}`
    },
    paymentDays() {
      return Number((this.customer_data && this.customer_data.payment) || 0)
    },
    today() {
      return moment().format('YYYY-MM-DD')
    },
    paymentOptions() {
      const days = [0, 7, 15, 30, 45, 60, 90]
      const current = Number(this.payment_days || 0)
      if (!days.includes(current)) days.push(current)
      return days.sort((a, b) => a - b).map(d => ({ value: d, text: d === 0 ? 'Contado' : `${d} días` }))
    },
    paymentDaysLabel() {
      if (!this.customer_data || this.customer_data.payment === undefined || this.customer_data.payment === null) {
        return 'Sin dato en maestro'
      }
      return this.paymentDays === 0 ? 'Contado' : `${this.paymentDays} días`
    },
    vencimientoHint() {
      if (!this.customer_data || !this.customer_data.id) return 'Se calcula con la condición de pago del cliente'
      if (Number(this.payment_days) === Number(this.paymentDays)) {
        return this.paymentDays === 0 ? 'Contado según maestro de clientes' : `${this.paymentDays} días del maestro de clientes`
      }
      return `Maestro de clientes: ${this.paymentDaysLabel}`
    }
  },
  methods: {
    onPaymentDaysChange(days) {
      this.fecha_pago = moment().add(Number(days || 0), 'days').format('YYYY-MM-DD')
    },
    onVencimientoChange(value) {
      const days = moment(value, 'YYYY-MM-DD').startOf('day').diff(moment().startOf('day'), 'days')
      this.payment_days = Math.max(days, 0)
    },
    removeItem(item) {
      this.productos.splice(this.productos.indexOf(item), 1)
    },
    confirm() {
      if (!this.sales_note.id) {
        notifications.showError('Debe cargar una orden de trabajo')
        return
      }
      if (this.tipo === 61 && !this.referencia_folio) {
        notifications.showError('La nota de crédito requiere folio de referencia')
        return
      }
      this.showConfirm = true
    },
    refresh() {
      if (this.sales_note.id) this.fetchData(this.sales_note.id)
    },
    applySalesNote(response) {
      const { sales_note_customer, details, totals } = response
      this.sales_note = response
      this.customer_data = sales_note_customer
      this.customer_data.addresses = (this.customer_data.addresses || []).map(a => ({
        text: `${a.street} ${a.number} ${a.comune} ${a.region}`,
        id: a.id
      }))
      if (this.customer_data.addresses.length > 0) this.direccion = this.customer_data.addresses[0]
      this.payment_days = this.paymentDays
      this.fecha_pago = moment().add(this.paymentDays, 'days').format('YYYY-MM-DD')
      const discountFactor = 1 - parseFloat(totals.discount || 0) / 100
      this.productos = (details.products || []).map(producto => {
        producto.price = parseFloat(producto.qty) != 0 ? parseFloat(producto.totalPrice / producto.qty) * discountFactor : 0
        producto.qty = parseFloat(producto.qty)
        return producto
      })
    },
    async fetchData(salesNoteId) {
      this.loading = true
      try {
        const response = await getSalesNote(salesNoteId)
        this.applySalesNote(response)
      } catch (error) {
        notifications.showError(error)
      } finally {
        this.loading = false
      }
    },
    async loadFromOt() {
      if (!this.otSearch) {
        notifications.showError('Indique el número de OT')
        return
      }
      this.loadingOt = true
      try {
        const workOrder = await getWorkOrder(this.otSearch)
        if (!workOrder || !workOrder.sales_note) {
          notifications.showError('La OT no tiene cotización asociada')
          return
        }
        this.loading = true
        const response = await getSalesNote(workOrder.sales_note)
        this.applySalesNote(response)
      } catch (error) {
        notifications.showError('No se encontró la orden de trabajo')
      } finally {
        this.loadingOt = false
        this.loading = false
      }
    },
    async sendData() {
      this.sending = true
      const data = {
        workorder_id: this.sales_note.work_order,
        tipo: this.tipo,
        direccion: this.direccion.id || this.direccion,
        neto: this.neto,
        iva: this.iva,
        observacion: `COT ${this.sales_note.id} - OT ${this.sales_note.work_order}`,
        fecha_pago: this.fecha_pago || moment().add(this.payment_days, 'days').format('YYYY-MM-DD'),
        payment_days: Number(this.payment_days || 0),
        fecha: moment().format('YYYY-MM-DD'),
        tipo_traslado: this.tipo_traslado,
        ind_traslado: this.tipo_traslado,
        tipo_despacho: this.tipo_despacho,
        detalle: this.productos.map(p => ({
          sku: p.sku,
          name: p.name,
          qty: p.qty,
          price: Math.round(p.price),
          unit: 'UN',
        })),
      }
      if (this.tipo === 61) {
        data.referencia_tipo = this.referencia_tipo
        data.referencia_folio = this.referencia_folio
        data.referencia_fecha = this.referencia_fecha
        data.motivo = this.motivo
        data.razon_ref = data.observacion
      }
      try {
        const response = await createDocument(data)
        if (response.status === 'Ok' || response.folio) {
          notifications.showSuccess(`Documento emitido en Facturación.cl. Folio: ${response.folio}`)
          this.showConfirm = false
          if (response.id) {
            try {
              await downloadDocumentPdf(response.id)
            } catch (err) {
              notifications.showError('El documento se emitió, pero no se pudo abrir el PDF')
            }
          }
          this.$emit('issued')
          this.$emit('close')
        } else {
          notifications.showError(response.error || 'Error al emitir documento')
        }
      } catch (e) {
        const message = typeof e === 'string' ? e : (e && e.error) || JSON.stringify(e)
        notifications.showError(message)
      } finally {
        this.sending = false
        this.showConfirm = false
      }
    }
  },
  async created() {
    if (this.sales_note_id) this.fetchData(this.sales_note_id)
  }
}
</script>
